import {
  urlHome,
  urlScanProduct,
  urlSelectCategory,
  urlSuccess,
} from "../../urls";
import { Link, useNavigate } from "react-router";
import { useCurrentPos } from "../../hooks/use-current-pos";
import {
  ArrowLeft,
  CheckCircle,
  LucideScanBarcode,
  Search,
} from "lucide-react";
import { Button } from "@repo/ui/components/button";
import { useSendEvent } from "../../hooks/use-send-event";
import { ScanItem, ScanItemProduct } from "../../components/scan-item";
import { useAtomValue } from "jotai";
import { playerAtom, productsAtom } from "../../stores/pos";
import type { CreateSaleItem } from "@repo/shared-types";
import { useMemo, useRef, useEffect } from "react";
import { useNetworkStatus } from "@repo/ui/hooks/use-network-status";
import { productApi } from "@repo/api-client";
import { savePendingSale } from "../../utils/pending-sales";
import { useProductScan } from "../../hooks/use-product-scan";

function ScanProduct() {
  const { club } = useCurrentPos();
  const navigate = useNavigate();
  const sendEvent = useSendEvent();
  const player = useAtomValue(playerAtom);
  const isOnline = useNetworkStatus();
  const products = useAtomValue(productsAtom);

  let productsAmount = 0;
  club.productCategories.forEach((category) => {
    productsAmount += category.products.filter(
      (product) => product.showOnPOS
    ).length;
  });

  useProductScan("scan-product");

  const onComplete = () => {
    const isPlayer = player && typeof player === "object";
    const saleItem: CreateSaleItem = {
      playerId: isPlayer ? player.id : undefined,
      name: !isPlayer ? (player ? player : undefined) : undefined,
      productIds: products.map((product) => product.id),
    };
    sendEvent("sale_complete", saleItem);
    if (isOnline) {
      productApi.createSaleItem(saleItem).catch((error) => {
        console.error("Error creating sale item:", error);
        savePendingSale(saleItem);
      });
    } else {
      savePendingSale(saleItem);
    }
    navigate(urlSuccess(), { viewTransition: true });
  };

  const playerName = useMemo(() => {
    if (!player) {
      return "";
    }
    if (typeof player === "string") {
      return player;
    }
    return player.name;
  }, [player]);

  const productsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (productsContainerRef.current && products.length > 0) {
      productsContainerRef.current.scrollTop =
        productsContainerRef.current.scrollHeight;
    }
  }, [products.length]);

  return (
    <div className="flex flex-col items-center justify-center gap-2 h-full w-full py-4">
      <div className="w-full relative flex items-center justify-center">
        <Button
          className="absolute left-0"
          asChild
          size="2xl"
          variant="pos-outline"
        >
          <Link to={urlHome()} viewTransition>
            <ArrowLeft className="size-4" />
            Volver al inicio
          </Link>
        </Button>
        {player && (
          <div className="flex flex-col items-center justify-center my-2 pb-2">
            <div className="text-3xl font-bold text-primary">
              Hola, <span className="capitalize">{playerName}</span>
            </div>
            <span className="text-lg text-gray-500 text-center">
              Escanea tus productos
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 flex flex-col items-center justify-start gap-2 bg-gray-100 rounded-xl p-4 w-full inset-shadow-sm shadow-gray-500/50 min-h-0">
        <ScanItem className="mb-4 shrink-0">
          <div className="flex items-center justify-center gap-2">
            <LucideScanBarcode className="size-6 text-gray-500" />
            <div className="text-md text-gray-500">Esperando escaneo...</div>
          </div>
          <div className="flex items-center justify-center gap-2">
            {productsAmount > 0 && (
              <>
                <Button
                  variant="secondary"
                  asChild
                  className="bg-gray-100 text-gray-500! border-gray-300! font-medium!"
                >
                  <Link to={urlSelectCategory()} viewTransition>
                    <Search className="size-4" />
                    Catálogo manual
                  </Link>
                </Button>
                <div className="flex-1 h-[30px] w-px bg-gray-200 mx-1" />
              </>
            )}
            <div className="size-2 rounded-full bg-green-500 animate-pulse shadow-xl shadow-green-500/50" />
          </div>
        </ScanItem>
        <div
          ref={productsContainerRef}
          className="flex-1 w-full overflow-y-auto overflow-x-hidden flex flex-col gap-2 min-h-0 px-1 py-1"
        >
          {products.map((product) => (
            <ScanItemProduct key={product.id} product={product} />
          ))}
        </div>
        <Button
          variant="pos"
          className="w-full"
          size="4xl"
          onClick={onComplete}
          disabled={products.length === 0}
        >
          Completar
          <CheckCircle className="size-5 stroke-2" />
        </Button>
      </div>
    </div>
  );
}

export const route = {
  path: urlScanProduct(),
  element: <ScanProduct />,
  handle: {
    backLink: urlHome(),
  },
};
